import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { formatShortDateTime } from '../utils/dateFormat';
import { useMaintenanceStatus } from '../hooks/useMaintenanceStatus';

export function MaintenanceBanner() {
  // Shared with the App-level maintenance guard — one 60s poll for both,
  // paused while the tab is hidden.
  const maintenance = useMaintenanceStatus();
  const status = maintenance?.status ?? 'none';
  const win = maintenance?.window ?? null;
  const [dismissed, setDismissed] = useState(false);
  const { isDark } = useTheme();
  const { t, language } = useLanguage();

  if (status === 'none' || dismissed || !win) return null;

  const isActive = status === 'active';
  const bannerStyles = isDark
    ? {
        background: isActive ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.1)',
        borderBottom: `1px solid ${isActive ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'}`,
        color: isActive ? '#FCA5A5' : '#FDE68A',
      }
    : {
        background: isActive ? '#FEF2F2' : '#FFF7E6',
        borderBottom: `1px solid ${isActive ? '#FECACA' : '#FCD34D'}`,
        color: isActive ? '#991B1B' : '#92400E',
      };

  const timeUntil = (iso: string) => {
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return t('maintenance.shortly');
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours > 0) {
      return t('maintenance.inHoursMinutes', { hours, minutes });
    }
    return t('maintenance.inMinutes', { minutes });
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3 text-sm font-medium" style={bannerStyles}>
      <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        {isActive ? (
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        )}
      </svg>

      <div className="flex-1 min-w-0">
        {isActive ? (
          <span>
            <strong>{win.title ?? t('maintenance.inProgress')}</strong>
            {win.message ? ` - ${win.message}` : ` - ${t('maintenance.unavailable')}`}
            {win.ends_at && <span className="ml-1 opacity-80">{t('maintenance.backAt', { value: formatShortDateTime(win.ends_at, language) })}</span>}
          </span>
        ) : (
          <span>
            <strong>{t('maintenance.scheduled', { value: timeUntil(win.starts_at) })}</strong>
            {win.message ? ` - ${win.message}` : ''}
            <span className="ml-1 opacity-80">
              {formatShortDateTime(win.starts_at, language)} {'->'} {formatShortDateTime(win.ends_at, language)}
            </span>
          </span>
        )}
      </div>

      {!isActive && (
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity ml-2"
          title={t('common.dismiss')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
